import React, { useMemo, useCallback, useEffect, useState } from "react";
import { Container, Box, Chip } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import Navbar from "../../Navbar";
import { useFechas } from "../pedidosEnvasado/context/FechaContext";
import { useTodosPedidos, calcularEstadisticas } from "./hooks/useTodosPedidos";
import { HeaderFiltros } from "./components/HeaderFiltros";
import { OrigenCard } from "./components/OrigenCard";
import { BarraDistribucion } from "./components/BarraDistribucion";
import { EstadoChips } from "./components/EstadoChips";
import { TableSkeleton } from "../facturados/components/TableSkeleton";
import { ErrorAlert } from "../facturados/components/ErrorAlert";
import { createColumns, createTableConfig } from "./utils/tableConfig";
import { TABLE_INITIAL_STATE, ORIGENES, ORIGEN_CONFIG, ESTADO_DESCARTADO } from "./constants";
import { verEnviarPDFenvasado } from "../../../services/services";

const MaterialReactTable = React.lazy(() => import("material-react-table"));

const ORIGENES_FIJOS = [ORIGENES.PEDIDO_NUEVO, ORIGENES.TELEFONISTAS, ORIGENES.HOJA_RUTA];

function TodosPedidos() {
  const { desde, hasta, setDesde, setHasta } = useFechas();
  const { pedidos, loading, error, obtenerPedidos, setError } = useTodosPedidos();

  const [incluirDescartados, setIncluirDescartados] = useState(false);
  const [filtroOrigen, setFiltroOrigen] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState(null);

  const columns = useMemo(() => createColumns(), []);

  // Carga inicial al montar el componente
  useEffect(() => {
    obtenerPedidos(desde, hasta);
  }, []); // Solo se ejecuta una vez al montar

  const handleActualizar = useCallback(() => {
    obtenerPedidos(desde, hasta);
  }, [obtenerPedidos, desde, hasta]);

  const handleVerFactura = useCallback((row) => {
    if (row.id_factura) {
      verEnviarPDFenvasado(row.id_factura, 0);
    } else {
      setError("Esta factura no está disponible");
    }
  }, [setError]);

  const handleVerNC = useCallback((row) => {
    if (row.id_nc) {
      verEnviarPDFenvasado(row.id_nc, 0);
    }
  }, []);

  const tableHandlers = useMemo(() => ({
    handleVerFactura,
    handleVerNC
  }), [handleVerFactura, handleVerNC]);

  const cantidadDescartados = useMemo(() =>
    pedidos.filter((p) => p.estado === ESTADO_DESCARTADO).length,
    [pedidos]
  );

  // Base de estadísticas y tabla según el switch de descartados
  const pedidosBase = useMemo(() =>
    incluirDescartados ? pedidos : pedidos.filter((p) => p.estado !== ESTADO_DESCARTADO),
    [pedidos, incluirDescartados]
  );

  const estadisticas = useMemo(() => calcularEstadisticas(pedidosBase), [pedidosBase]);

  // Un filtro que ya no tiene pedidos (ej: se apagó el switch) deja de aplicarse
  const origenActivo = estadisticas.porOrigen[filtroOrigen] ? filtroOrigen : null;
  const estadoActivo = estadisticas.porEstado[filtroEstado] ? filtroEstado : null;

  const pedidosTabla = useMemo(() =>
    pedidosBase.filter((p) =>
      (!origenActivo || (p.origen || ORIGENES.SIN_ORIGEN) === origenActivo) &&
      (!estadoActivo || (p.valor || "Sin estado") === estadoActivo)
    ),
    [pedidosBase, origenActivo, estadoActivo]
  );

  const tableConfig = useMemo(() =>
    createTableConfig(columns, pedidosTabla, tableHandlers, TABLE_INITIAL_STATE),
    [columns, pedidosTabla, tableHandlers]
  );

  // "Sin origen" solo se muestra si hay pedidos que no cumplen ningún criterio
  const origenes = useMemo(() => {
    const lista = [...ORIGENES_FIJOS];
    if (estadisticas.porOrigen[ORIGENES.SIN_ORIGEN]) lista.push(ORIGENES.SIN_ORIGEN);
    return lista;
  }, [estadisticas.porOrigen]);

  const cargandoInicial = loading && pedidos.length === 0;

  const renderTable = () => {
    if (cargandoInicial) {
      return <TableSkeleton />;
    }

    if (error) {
      return <ErrorAlert error={error} onClose={() => setError(null)} />;
    }

    return (
      <React.Suspense fallback={<TableSkeleton />}>
        <MaterialReactTable {...tableConfig} />
      </React.Suspense>
    );
  };

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <Navbar />

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <HeaderFiltros
          desde={desde}
          hasta={hasta}
          setDesde={setDesde}
          setHasta={setHasta}
          onActualizar={handleActualizar}
          loading={loading}
          incluirDescartados={incluirDescartados}
          setIncluirDescartados={setIncluirDescartados}
          cantidadDescartados={cantidadDescartados}
        />

        {/* Total y cantidad por origen; clic en un origen filtra la tabla */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            mb: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: `repeat(${origenes.length + 1}, 1fr)`,
            },
          }}
        >
          <OrigenCard
            titulo="Total"
            valor={estadisticas.total}
            hex="#5f6368"
            Icon={ListAltIcon}
            loading={cargandoInicial}
          />
          {origenes.map((origen) => (
            <OrigenCard
              key={origen}
              titulo={origen}
              valor={estadisticas.porOrigen[origen] || 0}
              total={estadisticas.total}
              hex={ORIGEN_CONFIG[origen].hex}
              Icon={ORIGEN_CONFIG[origen].Icon}
              selected={origenActivo === origen}
              onClick={() => setFiltroOrigen(origenActivo === origen ? null : origen)}
              loading={cargandoInicial}
            />
          ))}
        </Box>

        <BarraDistribucion
          origenes={origenes}
          porOrigen={estadisticas.porOrigen}
          total={estadisticas.total}
        />

        <EstadoChips
          porEstado={estadisticas.porEstado}
          seleccionado={estadoActivo}
          onSeleccionar={setFiltroEstado}
        />

        {(origenActivo || estadoActivo) && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            {origenActivo && (
              <Chip size="small" label={`Origen: ${origenActivo}`} onDelete={() => setFiltroOrigen(null)} />
            )}
            {estadoActivo && (
              <Chip size="small" label={`Estado: ${estadoActivo}`} onDelete={() => setFiltroEstado(null)} />
            )}
          </Box>
        )}

        {renderTable()}
      </Container>
    </Box>
  );
}

export default TodosPedidos;
